import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments';

export interface CreateShipmentRequest {
  treatmentId: string;
}

export interface ArchiveShippingLabelResponse {
  shippingLabelFileId: number;
}

export interface ValidateShippingAddressRequest {
  addressLine1: string;
  addressLine2?: string | null;
  city: string;
  provinceId: number;
  zipCode: string;
  countryId: number;
}

export interface ValidateShippingAddressResponse {
  isValid: boolean;
  message: string;
  suggestedAddress?: SuggestedShippingAddress | null;
}

export interface SuggestedShippingAddress {
  addressLine1: string;
  addressLine2?: string | null;
  city: string;
  stateCode: string;
  provinceId: number;
  zipCode: string;
}

export interface CreateShipmentResponse {
  treatmentId: string;
  trackingNumber: string;
  labelUrl?: string;
  encodedLabel?: string;
  serviceType: string;
  packagingType: string;
  status: string;
}

const basePath = `${environment.apiBaseUrl}shipment`;

@Injectable({ providedIn: 'root' })
export class ShipmentApiClient {
  private readonly _http = inject(HttpClient);

  /** Checks a checkout address with FedEx before an order is placed. */
  validateShippingAddress(address: ValidateShippingAddressRequest): Observable<ValidateShippingAddressResponse> {
    return this._http.post<ValidateShippingAddressResponse>(`${basePath}/validate-address`, address);
  }

  createShipment(request: CreateShipmentRequest): Observable<CreateShipmentResponse> {
    return this._http.post<CreateShipmentResponse>(`${basePath}/create`, request);
  }

  getShipment(treatmentId: string): Observable<CreateShipmentResponse> {
    return this._http.get<CreateShipmentResponse>(`${basePath}/${treatmentId}`);
  }

  /** Complete Lab Slip PDF with treatment info and shipping label. */
  getLabSlipPdf(treatmentId: string): Observable<Blob> {
    return this._http.get(`${basePath}/${treatmentId}/lab-slip-pdf`, { responseType: 'blob' });
  }

  /** Uploads the current FedEx label PDF to S3 (patient folder). */
  archiveShippingLabelPdfToS3(treatmentId: string): Observable<ArchiveShippingLabelResponse> {
    return this._http.post<ArchiveShippingLabelResponse>(
      `${basePath}/${treatmentId}/shipping-label-file/archive-s3`,
      {}
    );
  }
}
